import React, { useRef, useEffect } from "react";
import { Formik, FormikProps, FormikHelpers, useFormikContext } from "formik";
import * as yup from "yup";
import { Fields } from "./components";
import { createYupSchema } from "./utils/yupSchemaCreator";
import { FIELD_TYPES, VALIDATION_TYPES } from "./constants";
import * as CardModule from "@mui/material/Card/index.js";
import * as CardContentModule from "@mui/material/CardContent/index.js";
import * as GridModule from "@mui/material/Grid/index.js";
import * as TypographyModule from "@mui/material/Typography/index.js";
import { Template } from "../models";
import ResponsiveTypography from "../components/ResponsiveTypography";
import { resolveDefault } from "../utils/moduleInterop";

const Card = resolveDefault<any>(CardModule);
const CardContent = resolveDefault<any>(CardContentModule);
const Grid = resolveDefault<any>(GridModule);
const Typography = resolveDefault<any>(TypographyModule);

type FormValues = Record<string, any>;

const FormikErrorReporter: React.FC<{
	onValidationError: (hasErrors: boolean, errorFields: string[]) => void;
	fieldLabels: Record<string, string>;
}> = ({ onValidationError, fieldLabels }) => {
	const { errors, submitCount } = useFormikContext();

	useEffect(() => {
		if (submitCount > 0) {
			const errorKeys = Object.keys(errors);
			const errorFieldNames = errorKeys.map(
				(key) => fieldLabels[key] || key,
			);
			onValidationError(errorKeys.length > 0, errorFieldNames);
		}
	}, [errors, submitCount, onValidationError, fieldLabels]);

	return null;
};

interface TemplateRendererProps {
	template: Template;
	cbSubmit: (values: Record<string, any>) => void;
	formId: string;
	submitFormRef: any;
	formValues: FormValues;
	onValidationError?: (hasErrors: boolean, errorFields: string[]) => void;
	id?: string;
	profile?: any;
}

const TemplateRenderer: React.FC<TemplateRendererProps> = ({
	cbSubmit,
	template,
	formId,
	submitFormRef,
	profile,
	formValues,
	onValidationError,
}) => {
	const fields = template.fields;
	const initialValues: Record<string, any> = {};

	const fieldLabels: Record<string, string> = {};
	fields.forEach((item) => {
		fieldLabels[item.id] = item.label;
	});

	fields.forEach((item) => {
		if (!!item.value) {
			formValues[item.id] = item.value;
		} else if (!formValues[item.id] && !!item.defaultValue) {
			formValues[item.id] = item.defaultValue;
		} else {
			formValues[item.id] = "";
		}
	});

	const yupSchema = fields.reduce(createYupSchema, {});
	const validationSchema = yup.object().shape(yupSchema);

	return (
		<Card
			sx={{
				mb: 0.5,
				borderRadius: 1,
				marginLeft: "auto",
				marginRight: "auto",
			}}
		>
			<CardContent>
				{template.title && (
					<ResponsiveTypography>
						{"Template title " + template.title}
					</ResponsiveTypography>
				)}
				<Formik
					enableReinitialize={true}
					initialValues={formValues}
					validationSchema={validationSchema}
					onSubmit={(
						values: Record<string, any>,
						formikHelpers: FormikHelpers<Record<string, any>>,
					) => {
						cbSubmit(values);
						// formikHelpers.resetForm();
					}}
				>
					{(formikProps: FormikProps<Record<string, any>>) => {
						submitFormRef.current = formikProps.submitForm;

						return (
							<form onSubmit={formikProps.handleSubmit}>
								{" "}
								{onValidationError && (
									<FormikErrorReporter
										onValidationError={onValidationError}
										fieldLabels={fieldLabels}
									/>
								)}{" "}
								<Grid container spacing={1.5}>
									<Fields
										fields={fields}
										formikProps={formikProps}
										formId={formId}
										profile={profile}
									/>
								</Grid>
							</form>
						);
					}}
				</Formik>
			</CardContent>
		</Card>
	);
};

export default TemplateRenderer;
